import union from 'lodash/union';

import { Model, OneFormReduxState, ReduxState, ValidationResults, ValidationSchema } from '../types';
import { selectDirtyState } from './dirty-states';
import { selectModel } from './models';

export const storedValidators: {
	[dataKey: string]: ValidationSchema<string> | undefined;
} = {};

export const addValidator = (key: string, validationSchema: ValidationSchema<string>): void => {
	storedValidators[key] = validationSchema;
};

const defaultState = {
	isValid: true,
	fields: {} as Record<string, { isValid: boolean; errors: string[] } | undefined>,
};

const getValidationErrors = <FieldNames extends string>(
	schema: ValidationSchema<FieldNames>,
	formProps: OneFormReduxState<FieldNames>,
): ValidationResults<FieldNames> =>
	/*
    This is a re-implementation of https://github.com/davezuko/react-reformed/blob/49f36e3259e023587e28d3dec2400ac2b4431299/src/validateSchema.js
    That passes more context to the validation test function (specifically, the whole props object)
  */
	formProps.model
		? union(Object.keys(schema), Object.keys(formProps.model)).reduce((acc, key) => {
				const value = (formProps.model as Record<string, Model<FieldNames>[FieldNames] | undefined>)[key];
				const test = (schema as Record<string, (typeof schema)[FieldNames] | undefined>)[key];

				let errors: string[] = [];
				if (test) {
					let error;
					test(
						value,
						emittedError => {
							error = emittedError;
						},
						formProps,
					);
					errors = error ? (Array.isArray(error) ? error : [error]) : [];
				}

				return {
					...acc,
					isValid: !errors.length && acc.isValid,
					fields: {
						...acc.fields,
						[key]: {
							isValid: !errors.length,
							errors,
						},
					},
				};
			}, defaultState)
		: defaultState;

export const selectValidation =
	(dataKey: string): ((state: ReduxState) => ValidationResults<string>) =>
	state => {
		const model = selectModel(dataKey)(state);
		const dirtyState = selectDirtyState(dataKey)(state);
		const validationSchema = storedValidators[dataKey] ?? {};

		return getValidationErrors(validationSchema, {
			dirtyState,
			model,
		});
	};
